import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
  PocketRow,
  SelectField,
  ToggleChip,
} from "@/components/ui";
import type { SelectFieldOption } from "@/components/ui/select-field";
import { openSpokenLanguageSettings } from "@/desktop/spoken-language-settings";
import {
  AUTOMATIC_LOCALE,
  useDictationLocales,
} from "@/desktop/use-dictation-locales";
import { activeLocaleTag, useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { languageDisplayName } from "@/lib/language-display-name";
import { useEffectiveAppLanguage } from "@/stores/app-language-store";
import {
  spokenLanguagesOrDefault,
  useDictationSettingsStore,
} from "@/stores/dictation-settings-store";

const isEnglish = (tag: string) => tag.toLowerCase().split("-")[0] === "en";

/** The engine's languages named in the app language and sorted by those
 *  names, Automatic first. */
function namedLocales(
  locales: ReadonlyArray<{ id: string; name: string }>,
  automatic: string,
): SelectFieldOption[] {
  const collator = new Intl.Collator(activeLocaleTag());
  const named = locales
    .filter((locale) => locale.id !== AUTOMATIC_LOCALE)
    .map((locale) => ({
      value: locale.id,
      label: languageDisplayName(locale.id, locale.name),
    }))
    .sort((a, b) => collator.compare(a.label, b.label));
  return locales.some((locale) => locale.id === AUTOMATIC_LOCALE)
    ? [{ value: AUTOMATIC_LOCALE, label: automatic }, ...named]
    : named;
}

/** The languages always offered as chips; the others are under More. */
const FEATURED_LANGUAGES = ["en", "de", "zh"];
const CHIP_ICON = "h-3 w-3 shrink-0";

/**
 * While the language is Automatic: the languages the user speaks, which
 * Whisper detects each recording's language among. A chip per language,
 * on while it is one of them; the last one cannot be turned off. Onboarding's
 * voice step shows it too, where the languages also pick the dictation model.
 */
export function SpokenLanguagesRow({
  locales,
}: {
  locales: ReadonlyArray<{ id: string; name: string }>;
}) {
  const t = useT(settingsVoiceMessages);
  const appLanguage = useEffectiveAppLanguage();
  const saved = useDictationSettingsStore((s) => s.spokenLanguages);
  const setSpokenLanguages = useDictationSettingsStore(
    (s) => s.setSpokenLanguages,
  );
  const chosen = spokenLanguagesOrDefault(saved, appLanguage);
  const nameOf = (id: string) =>
    languageDisplayName(
      id,
      locales.find((locale) => locale.id === id)?.name ?? id,
    );
  const chips = [...new Set([...FEATURED_LANGUAGES, ...chosen])];
  const collator = new Intl.Collator(activeLocaleTag());
  const more = locales
    .filter((locale) => locale.id !== AUTOMATIC_LOCALE)
    .filter((locale) => !chips.includes(locale.id))
    .map((locale) => ({ id: locale.id, label: nameOf(locale.id) }))
    .sort((a, b) => collator.compare(a.label, b.label));
  const toggle = (id: string) =>
    setSpokenLanguages(
      chosen.includes(id)
        ? chosen.filter((language) => language !== id)
        : [...chosen, id],
    );
  return (
    <PocketRow
      testId="settings-row-voice-spoken-languages"
      title={t("languagesISpeak")}
      below={
        <div
          role="group"
          aria-label={t("languagesISpeak")}
          className="flex flex-wrap gap-1 pt-1.5"
        >
          {chips.map((id) => {
            const pressed = chosen.includes(id);
            const StateIcon = pressed ? Icons.Check : Icons.Plus;
            return (
              <ToggleChip
                key={id}
                pressed={pressed}
                disabled={pressed && chosen.length === 1}
                onClick={() => toggle(id)}
                testId={`voice-spoken-language-${id}`}
              >
                <StateIcon className={CHIP_ICON} aria-hidden="true" />
                {nameOf(id)}
              </ToggleChip>
            );
          })}
          {more.length > 0 ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <ToggleChip pressed={false} testId="voice-spoken-language-more">
                  <Icons.Plus className={CHIP_ICON} aria-hidden="true" />
                  {t("moreLanguages")}
                  <Icons.ChevronDown className={CHIP_ICON} aria-hidden="true" />
                </ToggleChip>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={6}
                className="scrollbar-none max-h-[320px] min-w-[170px] !overflow-y-auto"
              >
                {more.map((language) => (
                  <DropdownMenuItem
                    key={language.id}
                    onSelect={() => toggle(language.id)}
                    data-testid={`voice-spoken-language-option-${language.id}`}
                  >
                    <span className="truncate">{language.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      }
    />
  );
}

/** The value of the last entry under the platform recogniser: not a language,
 *  the way to add one in the system's settings. */
const ADD_LANGUAGES = "add-languages";

export interface VoiceLanguageRowViewProps {
  value: string;
  options: ReadonlyArray<SelectFieldOption>;
  secondLine?: string | null;
  onChange: (id: string) => void;
  title?: string;
  stacked?: boolean;
  variant?: "field" | "value";
}

/** Shared row without native locale discovery or settings actions. */
export function VoiceLanguageRowView({
  value,
  options,
  secondLine,
  onChange,
  title,
  stacked = false,
  variant = "value",
}: VoiceLanguageRowViewProps) {
  const t = useT(settingsVoiceMessages);
  const label = title ?? t("spokenLanguage");
  const select = (
    <SelectField
      variant={variant}
      value={value}
      valueLabel={value ? undefined : t("optionDefault")}
      options={options}
      onChange={onChange}
      testId="voice-language"
      ariaLabel={label}
    />
  );
  return (
    <PocketRow
      testId="settings-row-voice-language"
      title={label}
      secondLine={secondLine}
      trailing={stacked ? undefined : select}
      below={stacked ? select : undefined}
    />
  );
}

/**
 * Preferences › Voice: the language you speak, for all voice input (desktop).
 * The value is the chosen language, else the one the engine uses by default.
 * Voice commands stay English-only, so another language says so. The list is
 * the engine's own order; the platform recogniser's ends with a way to add
 * languages. Automatic, Whisper's, is followed by the languages it detects
 * among.
 */
export function VoiceLanguageRow({
  title,
  setup = false,
  stacked = false,
}: { title?: string; setup?: boolean; stacked?: boolean } = {}) {
  const t = useT(settingsVoiceMessages);
  const {
    available,
    locales,
    defaultId,
    selectedId,
    selectedLocale,
    choose,
    platformEngine,
  } = useDictationLocales();
  if (!available) return null;
  const missing =
    selectedId !== null && selectedLocale === null
      ? {
          value: selectedId,
          label: t("unavailableLanguage", { language: selectedId }),
        }
      : null;
  const value = selectedLocale?.id ?? missing?.value ?? defaultId ?? "";
  const line = missing
    ? t("missingLanguage")
    : value === AUTOMATIC_LOCALE
      ? t("automaticDetects")
      : value && !isEnglish(value)
        ? setup
          ? t("setupUsesEnglish")
          : t("commandsNeedEnglish")
        : null;
  return (
    <>
      <VoiceLanguageRowView
        value={value}
        title={title}
        stacked={stacked}
        secondLine={line}
        options={[
          ...namedLocales(locales, t("automatic")),
          ...(missing ? [missing] : []),
          ...(platformEngine
            ? [{ value: ADD_LANGUAGES, label: t("addLanguages") }]
            : []),
        ]}
        onChange={(id) =>
          id === ADD_LANGUAGES ? void openSpokenLanguageSettings() : choose(id)
        }
      />
      {value === AUTOMATIC_LOCALE ? (
        <SpokenLanguagesRow locales={locales} />
      ) : null}
    </>
  );
}
